import { TEXT_COLOR_CLASSES } from 'design-system';
import type { SVGAttributes } from 'types';
import { cn } from 'utils/classNames';
import { objectKeys } from 'utils/collection';

import type { IconName } from './names';

export type IconColor = keyof typeof TEXT_COLOR_CLASSES;
export const ICON_COLORS = objectKeys(TEXT_COLOR_CLASSES);

const SIZES = {
	12: 'size-3',
	16: 'size-4',
	20: 'size-5',
	24: 'size-6',
	28: 'size-7',
	32: 'size-8',
	36: 'size-9',
	40: 'size-10',
	44: 'size-11',
	48: 'size-12',
} as const;
export type IconSize = keyof typeof SIZES;
export const ICON_SIZES = objectKeys(SIZES);

export interface Props extends Omit<SVGAttributes<SVGElement>, 'children'> {
	/** Hide the icon from screen readers. */
	'aria-hidden'?: boolean;

	/** Additional class names for the SVG element. */
	'className'?: string;

	/** Color of the icon. Defaults to the current text color. */
	'color'?: IconColor;

	/** Name of the icon to be shown. */
	'name': IconName;

	/** Size of the icon, or null to disable sizing. */
	'size'?: IconSize | null;
}

/** Handles all the icons in the project. */
export default function Icon({
	'aria-hidden': ariaHidden = true,
	className,
	color,
	name,
	size = 24,
	...attrs
}: Props) {
	return (
		<svg
			width={24}
			height={24}
			className={cn(
				className,
				'inline-block',
				'shrink-0',
				'fill-current',
				'pointer-events-none',
				size && SIZES[size],
				color && TEXT_COLOR_CLASSES[color],
				name === 'loading' && 'animate-spinFast',
			)}
			aria-hidden={ariaHidden ? 'true' : undefined}
			{...attrs}
		>
			<use href={`#icon-${name}`} />
		</svg>
	);
}
Icon.displayName = 'Icon';
